// Library
import { useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { useFormik } from "formik"
import * as Yup from 'yup'

// Domain
import { IServiceChangePasswordRequest } from "../Domain/IServiceChangePassword"
import { IPropsScreen } from "../Domain/IPropsScreen"
import { IForm, initIForm } from "../Domain/IForm"

// Infraestructure
import { LanguageTranslate } from "../../../shared/Infraestructure/LanguageTranslate"
import { AdapterValidator } from "../../../shared/Infraestructure/AdapterValidator"
import { AdapterGenerico } from "../../../shared/Infraestructure/AdapterGenerico"
import { addLoading, hideIconMenu, removeLoading } from "../../../shared/Infraestructure/SliceGenerico"
import { RootState } from "../../../shared/Infraestructure/AdapterStore"
import { RepositoryImplMain } from "./RepositoryImplMain"

// UseCase
import { UseCaseChangePassword } from "../Application/UseCaseChangePassword"
import { UseCaseLogout } from "../Application/UseCaseLogout"
import { changePermisoVariable, signOut } from "../../../shared/Infraestructure/SliceAuthentication"

const passwordRules = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

export const Controller = (): IPropsScreen => {
    const { auth: { user }, generico: { websocket, dbLocal } } = useSelector((rootState: RootState) => rootState)
    const languageTranslate = LanguageTranslate();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const repository = new RepositoryImplMain(websocket, dbLocal, dispatch, '', '');

    const onInit = () => {
        if (!user) return navigate(`/${process.env.REACT_APP_ROUTE_PATH_LOGIN}`, { replace: true })
        onChange('usuario', user.username);
    }

    const form = useFormik<IForm>({
        initialValues: initIForm,
        onSubmit: () => {},
        validationSchema: Yup.object({
            usuario: Yup.string().required('Ingrese un Usuario'),
            currentPassword: Yup.string().required('Ingrese su Contraseña Actual'),
            newPassword: Yup.string().required('Ingrese una Nueva Contraseña')
                .matches(
                    passwordRules,
                    'Debe contener al menos 8 caracteres, una mayúscula, una minúscula, un número y un carácter especial'
                )
                .notOneOf(
                    [Yup.ref('currentPassword')],
                    'La nueva contraseña no puede ser igual a la actual'
                ),
            repeatPassword: Yup.string()
                .required('Confirme la nueva contraseña')
                .oneOf([Yup.ref('newPassword')], 'Las contraseñas no coinciden'),
        })
    })

    const onChange = (name: string, value: any) => {
        form.setFieldValue(name, value);
    }

    const onSubmit = async (evt: Event) => {
        try {
            dispatch(addLoading({}))
            evt.stopPropagation();
            evt.preventDefault();

            await form.submitForm();
            AdapterValidator.validate(await form.validateForm());
            const params: IServiceChangePasswordRequest = {
                IdUser: user.IdUser,
                Identificacion: user.Identificacion,
                Password1: form.values.newPassword,
                Email: user.Email,
                TypeLogin: user.TypeLogin,
                idSocket: 1,
                allData: user,
                Users: {
                    General: {
                        IdUser: user.IdUser,
                        LastName: `${user.LastNameFather} ${user.LastNameMother}`,
                        Name: user.Name,
                        Perfil: user.Profile?.[0]?.Perfil,
                        User: user.User
                    }
                }
            }

            await new UseCaseChangePassword(repository).exec(params)
            dispatch(removeLoading());
            await AdapterGenerico.createMessage('Exitoso', 'Su contraseña se actualizó correctamente.', 'success');
            await onClickLogout();
        } catch(error) {
            AdapterGenerico.createMessage(languageTranslate.textoAlerta, (error as Error).message, 'warning', false);
        } finally {
            dispatch(removeLoading());
        }
    }

     const onClickLogout = async () => {
        new UseCaseLogout(repository).exec();
        dispatch(hideIconMenu());
        dispatch(signOut());
        dispatch(changePermisoVariable({ arrIdPaises: [], arrIdGrupos: [], arrIdDelegaciones: [], arrIdOT: [] }));
        navigate(`/${process.env.REACT_APP_ROUTE_PATH_LOGIN}`, { replace: true });
    };

    return ({
        form,
        onChange,
        onSubmit,
        user,
        onInit
    })
}